import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { FixedSizeList as List } from "react-window";
import { Combobox, Transition } from "@headlessui/react";
import { getBadgeClassByPolicyStatus } from "js/formatting";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";

import Tooltip from "../Tooltip";

export default function AgentAutoCompleteComboBox({
  labelName = "Quick Filter",
  tooltipVerbiage,
  inputPlaceholderText = "Search by keyword",
  options = [],
  invalidMessage = "Nothing Found",
  selectedOption,
  setSelectedOption,
  readOnly = false,
  isDisabled = false,
  virtualizationThreshold = 300,
}) {
  const [selected, setSelected] = useState(selectedOption);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const debounceTimerRef = useRef();
  const [showOption, setShowOption] = useState(false);
  const itemHeight = 40;
  const viewportHeight = 320;

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 150);
    return () => clearTimeout(debounceTimerRef.current);
  }, [query]);

  const normalizedOptions = useMemo(
    () =>
      options.map((opt) => {
        const base = opt.forms || opt.displayAs || "";
        return {
          ...opt,
          _search: base.toLowerCase().replace(/\s+/g, ""),
          _parts: opt.displayAs ? opt.displayAs.split(":") : [],
        };
      }),
    [options]
  );

  const filteredOptions = useMemo(() => {
    if (!debouncedQuery) return normalizedOptions;
    const q = debouncedQuery.toLowerCase().replace(/\s+/g, "");
    return normalizedOptions.filter((o) => o._search.includes(q));
  }, [debouncedQuery, normalizedOptions]);

  const useVirtualization = filteredOptions.length > virtualizationThreshold;

  const Row = useCallback(
    ({ index, style }) => {
      const option = filteredOptions[index];
      if (!option?.displayAs) return null;
      return (
        <div style={style}>
          <Combobox.Option
            key={option.value}
            className={({ selected, active }) =>
              `relative cursor-default select-none p-[8px] ${
                selected
                  ? "bg-ui-blue text-white rounded-sm"
                  : active
                  ? "bg-ui-blue-light text-body rounded-sm"
                  : "text-body"
              }`
            }
            value={option}
            disabled={option?.isInActive}
          >
            {({ selected }) => (
              <div className="flex items-center h-full">
                <span
                  className={`block pr-5 truncate ${
                    selected
                      ? "font-normal"
                      : `font-semibold ${
                          option?.isInActive
                            ? "text-[color:var(--ui-gray-light)]"
                            : ""
                        }`
                  }`}
                >
                  {(() => {
                    const parts = option?._parts || [];
                    if (parts.length === 2) {
                      return (
                        <>
                          <span className="font-semibold">{parts[0]}:</span>
                          <span className="font-normal">{parts[1]}</span>
                        </>
                      );
                    } else {
                      return (
                        <span className="font-semibold">
                          {option?.displayAs}
                        </span>
                      );
                    }
                  })()}
                </span>
                {option?.status && (
                  <span className="absolute inset-y-0 right-2 flex items-center">
                    {getBadgeIcone(option)}
                  </span>
                )}
              </div>
            )}
          </Combobox.Option>
        </div>
      );
    },
    [filteredOptions]
  );

  useEffect(() => {
    setSelected(
      options.find(
        (option) =>
          option.value ===
          (typeof selectedOption === "object"
            ? selectedOption.value
            : selectedOption)
      )
    );
  }, [options, selectedOption]);
  const getBadgeIcone = (policy) => {
    const badgeClass = getBadgeClassByPolicyStatus(policy);
    const iconMap = {
      good: "#ui-icon-active",
      risk: "#ui-icon-grace",
      problem: "#ui-icon-inactive",
    };
    return (
      <svg
        className="badge-icon"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <use href={iconMap[badgeClass]}></use>
      </svg>
    );
  };

  return (
    <Combobox
      value={selected}
      onChange={(value) => {
        setSelectedOption(value);
        setSelected(value);
        setShowOption(false);
      }}
    >
      {({ activeIndex, activeOption, disabled, open }) => (
        <div className="relative mt-1">
          {labelName && (
            <div className="flex items-center mb-1">
              <Combobox.Label>{labelName}</Combobox.Label>
              {tooltipVerbiage && (
                <Tooltip xMargin="mx-1" content={tooltipVerbiage} />
              )}
            </div>
          )}

          <div className="relative">
            <Combobox.Input
              className="block truncate"
              placeholder={inputPlaceholderText}
              // perform search
              onChange={(event) => {
                setQuery(event.target.value);
                setShowOption(true);
              }}
              // clear on focus
              onClick={(e) => {
                e.target.value = "";
                setShowOption(true);
              }}
              // re-apply value when not selected
              onBlur={(e) => {
                e.target.value =
                  selected?.displayAsValue ?? options[0]?.displayAsValue;
                if (
                  (!activeOption?.value ||
                    activeOption?.value === selected?.value) &&
                  !activeOption?.isInActive
                ) {
                  setShowOption(false);
                }
              }}
              // control what gets to be on display when selected an option
              displayValue={(option) => option?.displayAsValue}
            />
            {!readOnly && (
              <span className="comboBox-caret">
                <>
                  {showOption ? (
                    <ChevronUpIcon
                      className="h-5 w-5 text-blue-400"
                      aria-hidden="true"
                    />
                  ) : (
                    <ChevronDownIcon
                      className="h-5 w-5 text-blue-400"
                      aria-hidden="true"
                    />
                  )}
                </>
              </span>
            )}
          </div>
          <Transition
            show={showOption}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              static
              className={`absolute mt-1 max-h-80 w-full rounded-md bg-white pl-2 py-2 text-base shadow-lg sm:text-sm z-10 ${
                useVirtualization ? "overflow-visible" : "overflow-auto"
              }`}
            >
              {filteredOptions.length === 0 && debouncedQuery !== "" ? (
                <div className="relative cursor-default select-none py-2 px-4">
                  {invalidMessage}
                </div>
              ) : useVirtualization ? (
                <List
                  height={viewportHeight}
                  width={"100%"}
                  itemCount={filteredOptions.length}
                  itemSize={itemHeight}
                  outerElementType="div"
                >
                  {Row}
                </List>
              ) : (
                filteredOptions.map(
                  (option) =>
                    option?.displayAs && (
                      <Combobox.Option
                        key={option.value}
                        className={({ selected, active }) =>
                          `relative cursor-default select-none p-[8px] ${
                            selected
                              ? "bg-ui-blue text-white rounded-sm"
                              : active
                              ? "bg-ui-blue-light text-body rounded-sm"
                              : "text-body"
                          }`
                        }
                        value={option}
                        disabled={option?.isInActive}
                      >
                        {({ selected }) => (
                          <div>
                            <span
                              className={`block pr-5 truncate ${
                                selected
                                  ? "font-normal"
                                  : `font-semibold ${
                                      option?.isInActive
                                        ? "text-[color:var(--ui-gray-light)]"
                                        : ""
                                    }`
                              }`}
                            >
                              {(() => {
                                const parts = option?._parts || [];
                                if (parts.length === 2) {
                                  return (
                                    <>
                                      <span className="font-semibold">
                                        {parts[0]}:
                                      </span>
                                      <span className="font-normal">
                                        {parts[1]}
                                      </span>
                                    </>
                                  );
                                } else {
                                  return (
                                    <span className="font-semibold">
                                      {option?.displayAs}
                                    </span>
                                  );
                                }
                              })()}
                            </span>
                            {option?.status && (
                              <span className="absolute inset-y-0 right-2 flex items-center">
                                {getBadgeIcone(option)}
                              </span>
                            )}
                          </div>
                        )}
                      </Combobox.Option>
                    )
                )
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}
